// app/src/modules/ui/primitives.tsx — the component library's data + layout
// primitives (UI-BUILD-PLAN §3). These are the ONLY sanctioned way to render
// money, quantities, plates, references, statuses, role-gated content and
// titled card sections on new or migrated screens. Everything reads the
// existing 78 design tokens — nothing here restyles the theme.
import { useEffect, useId, useRef } from "react";
import type { CSSProperties, ReactNode } from "react";
import { useSession } from "../../lib/session";

/* ── RoleGate ─────────────────────────────────────────────────────────────
   Role visibility as a component concern: costs, margins and destructive
   actions render only for holders of the named permission(s), so a screen
   can't accidentally leak them to a future cashier/technician login.
   `need` = one permission key or several (ANY grants). Server-side checks
   remain the enforcement; this controls what is drawn. */
export function useCan(): (need: string | string[]) => boolean {
  const { user } = useSession();
  return (need) => {
    if (!user) return false;
    const keys = Array.isArray(need) ? need : [need];
    return keys.some((k) => user.permissions.includes(k));
  };
}

export function RoleGate({ need, children, otherwise = null }:
  { need: string | string[]; children: ReactNode; otherwise?: ReactNode }) {
  const can = useCan();
  return <>{can(need) ? children : otherwise}</>;
}

/* ── Data primitives ──────────────────────────────────────────────────────
   One rendering of each business value type. All numerals are tabular via
   the existing .num token class. */

/** Money in minor units. `signed` shows an explicit + on positives (deltas). */
export function Money({ minor, symbol = "£", decimals = 2, signed = false, muted = false, style }:
  { minor: number; symbol?: string; decimals?: number; signed?: boolean; muted?: boolean; style?: CSSProperties }) {
  const sign = minor < 0 ? "-" : signed && minor > 0 ? "+" : "";
  const abs = Math.abs(minor);
  const f = Math.pow(10, decimals);
  const txt = `${sign}${symbol}${Math.floor(abs / f)}.${String(abs % f).padStart(decimals, "0")}`;
  return <span className="num" style={{ color: muted ? "var(--muted)" : undefined, ...style }}>{txt}</span>;
}

/** Quantity — trims trailing zeros but never rounds (yield quantities are 3dp). */
export function Qty({ value, unit, style }: { value: number; unit?: string; style?: CSSProperties }) {
  const txt = Number.isInteger(value) ? String(value) : String(Math.round(value * 1000) / 1000);
  return <span className="num" style={style}>{txt}{unit ? <span style={{ color: "var(--muted)", fontSize: "0.85em" }}> {unit}</span> : null}</span>;
}

/** UK registration plate — the system's one decorative element (plan §2):
    in a workshop the plate IS the bike's identity. */
export function Plate({ reg, size = 14 }: { reg?: string | null; size?: number }) {
  const r = (reg ?? "").trim().toUpperCase();
  if (!r) return <span className="t-muted">no reg</span>;
  const placeholder = r.startsWith("ABC-");
  return (
    <span className="num" style={{
      display: "inline-block", padding: "1px 7px", borderRadius: 4, fontSize: size, fontWeight: 700,
      letterSpacing: ".04em", lineHeight: 1.5, whiteSpace: "nowrap",
      background: placeholder ? "var(--paper-2)" : "#FFD84D", color: placeholder ? "var(--muted)" : "#111",
      border: placeholder ? "1px dashed var(--line)" : "1px solid #111",
    }} title={placeholder ? "Placeholder — bike not yet identified" : undefined}>{r}</span>
  );
}

/** Document reference (receipt/job/PO number) — mono, copy-friendly, never wraps. */
export function Ref({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <span className="num text-nowrap" style={{ fontSize: "0.95em", ...style }}>{children}</span>;
}

/** Compact elapsed/age text from an epoch-ms timestamp: 3m · 4h · 2d · 12 Aug. */
export function Elapsed({ ms }: { ms: number }) {
  const d = Date.now() - ms;
  const txt = d < 60_000 ? "now"
    : d < 3_600_000 ? `${Math.floor(d / 60_000)}m`
    : d < 86_400_000 ? `${Math.floor(d / 3_600_000)}h`
    : d < 14 * 86_400_000 ? `${Math.floor(d / 86_400_000)}d`
    : new Date(ms).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  return <span title={new Date(ms).toLocaleString("en-GB")} className="t-muted">{txt}</span>;
}

/* ── StatusPill ───────────────────────────────────────────────────────────
   Domain states → one visual language. Colour is never the only signal —
   the label always renders. Unknown states fall back to neutral. */
const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
  // good
  completed: { bg: "var(--green-soft, rgba(46,160,67,.12))", fg: "var(--green)" },
  invoiced: { bg: "var(--green-soft, rgba(46,160,67,.12))", fg: "var(--green)" },
  collected: { bg: "var(--green-soft, rgba(46,160,67,.12))", fg: "var(--green)" },
  paid: { bg: "var(--green-soft, rgba(46,160,67,.12))", fg: "var(--green)" },
  received: { bg: "var(--green-soft, rgba(46,160,67,.12))", fg: "var(--green)" },
  approved: { bg: "var(--green-soft, rgba(46,160,67,.12))", fg: "var(--green)" },
  active: { bg: "var(--green-soft, rgba(46,160,67,.12))", fg: "var(--green)" },
  // attention
  open: { bg: "var(--gold-soft, rgba(212,160,23,.14))", fg: "var(--gold)" },
  in_progress: { bg: "var(--gold-soft, rgba(212,160,23,.14))", fg: "var(--gold)" },
  diagnosing: { bg: "var(--gold-soft, rgba(212,160,23,.14))", fg: "var(--gold)" },
  awaiting_parts: { bg: "var(--gold-soft, rgba(212,160,23,.14))", fg: "var(--gold)" },
  partially_paid: { bg: "var(--gold-soft, rgba(212,160,23,.14))", fg: "var(--gold)" },
  estimate: { bg: "var(--gold-soft, rgba(212,160,23,.14))", fg: "var(--gold)" },
  pending: { bg: "var(--gold-soft, rgba(212,160,23,.14))", fg: "var(--gold)" },
  // critical / terminal
  overdue: { bg: "rgba(187,45,34,.10)", fg: "var(--accent-text, var(--accent))" },
  failed: { bg: "rgba(187,45,34,.10)", fg: "var(--accent-text, var(--accent))" },
  refunded: { bg: "rgba(187,45,34,.10)", fg: "var(--accent-text, var(--accent))" },
  voided: { bg: "rgba(187,45,34,.10)", fg: "var(--accent-text, var(--accent))" },
  cancelled: { bg: "var(--paper-2)", fg: "var(--muted)" },
  suspended: { bg: "var(--paper-2)", fg: "var(--muted)" },
  archived: { bg: "var(--paper-2)", fg: "var(--muted)" },
};
export function StatusPill({ status }: { status: string }) {
  const t = STATUS_TONE[status] ?? { bg: "var(--paper-2)", fg: "var(--ink-2)" };
  return (
    <span className="text-nowrap" style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 12, fontWeight: 600,
      background: t.bg, color: t.fg, letterSpacing: ".01em",
    }}>{status.replace(/_/g, " ")}</span>
  );
}

/* ── Panel ────────────────────────────────────────────────────────────────
   The titled card section every screen hand-builds today: header with title,
   optional count + action slot, body. Replaces bespoke card divs. */
export function Panel({ title, sub, count, action, children, bodyPad = true }:
  { title: ReactNode; sub?: ReactNode; count?: number | string; action?: ReactNode; children: ReactNode; bodyPad?: boolean }) {
  return (
    <div className="card mb-3">
      <div className="card-header py-2 d-flex align-items-center justify-content-between" style={{ gap: "var(--sp-3)" }}>
        <div className="grow">
          <span className="fw-semibold">{title}</span>
          {count !== undefined && <span className="num ms-2 t-meta" >{count}</span>}
          {sub && <div className="t-meta">{sub}</div>}
        </div>
        {action && <div className="d-flex align-items-center" style={{ gap: "var(--sp-2)", flexShrink: 0 }}>{action}</div>}
      </div>
      <div className={bodyPad ? "card-body p-3" : "card-body p-0"}>{children}</div>
    </div>
  );
}

/* ── ModalShell ───────────────────────────────────────────────────────────
   The screens grew ~19 hand-rolled overlays: a fixed div, a card, and a click
   handler. None announced themselves to a screen reader, none closed on
   Escape, and focus stayed behind on the page underneath — so keyboard users
   tabbed into hidden content. This wraps that pattern once, correctly:
   role="dialog" + aria-modal, labelled by its own title, Escape to close,
   focus moved in on open and restored to the trigger on close, Tab cycled
   inside, backdrop click to dismiss, and body scroll locked. Visuals match
   the existing overlays exactly — this is semantics, not a restyle. */
export function ModalShell({ title, onClose, children, width = 560, labelOnly = false }:
  { title: string; onClose: () => void; children: ReactNode; width?: number; labelOnly?: boolean }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    const restoreTo = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus the first control, or the panel itself when there is none.
    const focusables = () => Array.from(ref.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])') ?? []);
    const f = focusables();
    (f[0] ?? ref.current)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key !== "Tab" || !ref.current) return;
      const list = focusables();
      if (!list.length) return;
      const first = list[0], last = list[list.length - 1], active = document.activeElement as HTMLElement | null;
      if (!ref.current.contains(active)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      restoreTo?.focus?.();
    };
  }, [onClose]);

  return (
    <div role="presentation" onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "flex-start",
               justifyContent: "center", padding: "max(20px, env(safe-area-inset-top)) 16px 40px", zIndex: 50, overflowY: "auto" }}>
      <div ref={ref} tabIndex={-1} role="dialog" aria-modal="true"
        {...(labelOnly ? { "aria-label": title } : { "aria-labelledby": titleId })}
        onClick={(e) => e.stopPropagation()}
        style={{ width: `min(${width}px, 100%)`, borderRadius: "var(--r-lg)", border: "1px solid var(--line)",
                 background: "var(--card)", padding: 24, boxShadow: "var(--shadow-pop)", outline: "none",
                 maxHeight: "90vh", overflowY: "auto" }}>
        {!labelOnly && <div id={titleId} className="display" style={{ fontSize: 20, marginBottom: 10 }}>{title}</div>}
        {children}
      </div>
    </div>
  );
}

/* ── useModalDismiss ──────────────────────────────────────────────────────
   Roughly nineteen hand-rolled overlays predate ModalShell. Rewriting their
   JSX would mean restructuring live till and workshop screens for a
   behavioural fix, which is a poor risk trade. This hook gives an existing
   overlay the three things it is actually missing — Escape to close, focus
   restored to whatever opened it, and the page behind locked from scrolling —
   in one line, with no change to markup or appearance.

   New modals should still use ModalShell, which additionally provides the
   dialog role and a focus trap. */
export function useModalDismiss(onClose: () => void, active = true) {
  useEffect(() => {
    if (!active) return;
    const restoreTo = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      restoreTo?.focus?.();
    };
  }, [onClose, active]);
}

/** A name with its distinguishing specs on the same line, divided by " · ".
 *
 *  "2DP Front Brake Pads N-Max · Original", "2017 Honda Vision · scooter ·
 *  White · 110cc · AY19FJK". The name is bold and the specs are muted, so the
 *  eye lands on the name and reads the specs as qualification, not as a second
 *  title. One component so a product, a bike and a customer all read the same
 *  way; before this, Catalogue did it inline and Bikes put the specs on a
 *  second line, which is two treatments of one idea. */
export function NameWithSpec({ name, specs, size = 14 }: { name: React.ReactNode; specs?: Array<string | number | null | undefined | false>; size?: number }) {
  const parts = (specs ?? []).filter((s): s is string | number => s !== null && s !== undefined && s !== false && s !== "");
  return (
    <span style={{ fontSize: size, lineHeight: 1.25, minWidth: 0 }}>
      <span style={{ fontWeight: 800, color: "var(--ink)" }}>{name}</span>
      {parts.length > 0 && (
        <span style={{ color: "var(--muted)", fontWeight: 500, fontSize: Math.max(11, size - 2) }}>
          {parts.map((p, i) => <span key={i}> · {p}</span>)}
        </span>
      )}
    </span>
  );
}
